<template>
  <el-dialog
    title="选择行业"
    :visible.sync="dialogVisible"
    width="720px"
    custom-class="business-category-picker-dialog"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <div v-if="draftBcId" class="selected-bar">
      <span class="selected-label">已选：</span>
      <el-tag
        closable
        size="small"
        class="selected-tag"
        @close="clearSelection"
      >{{ selectedPreviewText }}</el-tag>
    </div>

    <div class="picker-body">
      <div class="primary-panel">
        <div class="panel-title">一级行业</div>
        <div class="primary-list">
          <div
            v-for="item in businessList"
            :key="getItemKey(item)"
            class="primary-item"
            :class="{ active: draftBcId === getItemKey(item) }"
            @click="selectPrimary(getItemKey(item))"
          >
            <span class="primary-item__label">{{ getItemLabel(item) }}</span>
            <i v-if="draftBcId === getItemKey(item)" class="el-icon-check primary-item__check" />
          </div>
        </div>
      </div>

      <div class="sub-panel">
        <div class="panel-title">
          子行业<span v-if="activePrimaryLabel">（{{ activePrimaryLabel }}）</span>
        </div>
        <div v-if="draftSubList.length" class="sub-grid">
          <div
            v-for="item in draftSubList"
            :key="getItemKey(item)"
            class="sub-item"
            :class="{ active: draftBcSubId === getItemKey(item) }"
            @click="selectSub(getItemKey(item))"
          >
            <span>{{ getItemLabel(item) }}</span>
            <i v-if="draftBcSubId === getItemKey(item)" class="el-icon-check" />
          </div>
        </div>
        <div v-else class="sub-empty">暂无子行业</div>
      </div>
    </div>

    <div slot="footer" class="picker-footer">
      <el-button class="btn-reset" @click="handleReset">重置</el-button>
      <el-button type="primary" class="btn-confirm" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'BusinessCategoryPicker',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    businessList: {
      type: Array,
      default: () => []
    },
    subBusinessFallback: {
      type: Array,
      default: () => []
    },
    bcId: {
      type: [String, Number],
      default: ''
    },
    bcSubId: {
      type: [String, Number],
      default: ''
    },
    bcSubName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      draftBcId: '',
      draftBcSubId: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    draftSubList() {
      return this.getSubListByBcId(this.draftBcId)
    },
    activePrimaryLabel() {
      if (!this.draftBcId) return ''
      const item = this.businessList.find(
        row => this.getItemKey(row) === String(this.draftBcId)
      )
      return item ? this.getItemLabel(item) : ''
    },
    selectedPreviewText() {
      if (!this.draftBcId) return ''
      let text = this.activePrimaryLabel
      if (this.draftBcSubId) {
        const sub = this.draftSubList.find(
          row => this.getItemKey(row) === String(this.draftBcSubId)
        )
        if (sub) {
          text += ` - ${this.getItemLabel(sub)}`
        }
      }
      return text
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initDraft()
      }
    }
  },
  methods: {
    getItemKey(item) {
      return String(item.key != null ? item.key : item.id)
    },
    getItemLabel(item) {
      return item.value || item.name || ''
    },
    getBusinessChildren(parentKey) {
      const parent = this.businessList.find(
        item => this.getItemKey(item) === String(parentKey)
      )
      if (!parent) return []
      return parent.children || parent.list || parent.subList || []
    },
    getSubListByBcId(bcId) {
      if (!bcId) return []
      let subList = this.getBusinessChildren(bcId)
      if (!subList.length && this.subBusinessFallback.length) {
        subList = this.subBusinessFallback.filter(item => {
          const parentKey = String(
            item.parentKey != null
              ? item.parentKey
              : item.businessId != null
                ? item.businessId
                : item.pid
          )
          return parentKey === String(bcId)
        })
      }
      return subList
    },
    initDraft() {
      this.draftBcId = this.bcId ? String(this.bcId) : ''
      this.draftBcSubId =
        this.bcSubId && this.bcSubId !== 0 && this.bcSubId !== '0'
          ? String(this.bcSubId)
          : ''
      if (!this.draftBcSubId) {
        this.inferSubIdFromName()
      }
      this.validateDraftSub()
    },
    inferSubIdFromName() {
      const subName = (this.bcSubName || '').trim()
      if (!subName || !this.draftBcId) return
      const subList = this.getSubListByBcId(this.draftBcId)
      const sub = subList.find(item => this.getItemLabel(item) === subName)
      if (sub) {
        this.draftBcSubId = this.getItemKey(sub)
      }
    },
    validateDraftSub() {
      let validKeys = this.draftSubList.map(item => this.getItemKey(item))
      if (this.draftBcSubId && !validKeys.includes(String(this.draftBcSubId))) {
        this.inferSubIdFromName()
      }
      validKeys = this.draftSubList.map(item => this.getItemKey(item))
      if (this.draftBcSubId && !validKeys.includes(String(this.draftBcSubId))) {
        this.draftBcSubId = ''
      }
    },
    selectPrimary(bcId) {
      if (this.draftBcId === bcId) return
      this.draftBcId = bcId
      this.draftBcSubId = ''
    },
    selectSub(bcSubId) {
      this.draftBcSubId = bcSubId
    },
    clearSelection() {
      this.draftBcId = ''
      this.draftBcSubId = ''
    },
    handleReset() {
      this.clearSelection()
    },
    handleConfirm() {
      if (!this.draftBcId) {
        this.$message.warning('请选择一级行业')
        return
      }
      if (this.draftSubList.length && !this.draftBcSubId) {
        this.$message.warning('请选择子行业')
        return
      }
      this.$emit('confirm', {
        bcId: this.draftBcId,
        bcSubId: this.draftBcSubId || ''
      })
      this.dialogVisible = false
    },
    handleClosed() {
      this.initDraft()
    }
  }
}
</script>

<style lang="less">
.business-category-picker-dialog {
  .el-dialog__header {
    padding: 20px 24px 12px;
    border-bottom: 1px solid #ebeef5;
  }

  .el-dialog__title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .el-dialog__body {
    padding: 0 24px 8px;
  }

  .el-dialog__footer {
    padding: 16px 24px 24px;
    border-top: none;
  }
}
</style>

<style lang="less" scoped>
.selected-bar {
  display: flex;
  align-items: center;
  margin: 16px 0;
  padding: 10px 16px;
  background: #eef6ff;
  border-radius: 4px;

  .selected-label {
    flex-shrink: 0;
    margin-right: 8px;
    color: #606266;
    font-size: 14px;
  }

  .selected-tag {
    color: #409eff;
    background: #fff;
    border-color: #d9ecff;
  }
}

.picker-body {
  display: flex;
  min-height: 360px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

.primary-panel {
  flex-shrink: 0;
  width: 200px;
  border-right: 1px solid #ebeef5;
  background: #fafafa;
}

.panel-title {
  padding: 14px 16px 10px;
  color: #909399;
  font-size: 13px;
  line-height: 1;
}

.primary-list {
  max-height: 320px;
  overflow-y: auto;
}

.primary-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  color: #303133;
  font-size: 14px;
  cursor: pointer;
  border-left: 3px solid transparent;
  transition: all 0.2s;

  &:hover {
    background: #f5f7fa;
  }

  &.active {
    color: #409eff;
    background: #f5f9ff;
    border-left-color: #409eff;
    font-weight: 500;
  }

  &__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__check {
    flex-shrink: 0;
    margin-left: 8px;
    font-size: 14px;
    font-weight: bold;
  }
}

.sub-panel {
  flex: 1;
  min-width: 0;
  padding-bottom: 16px;
  background: #fff;
}

.sub-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 16px;
}

.sub-item {
  display: inline-flex;
  align-items: center;
  min-width: 108px;
  padding: 8px 14px;
  color: #303133;
  font-size: 14px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  span {
    margin-right: 4px;
  }

  &:hover {
    border-color: #c6e2ff;
    color: #409eff;
  }

  &.active {
    color: #409eff;
    border-color: #409eff;
    background: #f5f9ff;
  }

  .el-icon-check {
    font-size: 12px;
    font-weight: bold;
  }
}

.sub-empty {
  padding: 40px 16px;
  color: #c0c4cc;
  font-size: 14px;
  text-align: center;
}

.picker-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .btn-reset {
    min-width: 88px;
    color: #606266;
    border-color: #dcdfe6;
    border-radius: 20px;
  }

  .btn-confirm {
    min-width: 88px;
    border-radius: 20px;
  }
}
</style>
